import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, CreditCard, Landmark, Link2, RefreshCw, Unplug, Upload, WalletCards, WandSparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { parseBankCsv } from '../lib/bankCsv';
import { usePlan } from '../contexts/PlanContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';

const money = (value, currency = 'CAD') => new Intl.NumberFormat('en-CA', {
  style: 'currency', currency,
}).format(Number(value || 0));

const statusStyle = {
  matched: 'bg-emerald-100 text-emerald-800',
  suggested: 'bg-amber-100 text-amber-800',
  recorded: 'bg-blue-100 text-blue-800',
  ambiguous: 'bg-amber-100 text-amber-800',
  duplicate: 'bg-slate-200 text-slate-700',
  unmatched: 'bg-rose-100 text-rose-800',
  ignored: 'bg-slate-100 text-slate-500',
  outgoing: 'bg-violet-100 text-violet-800',
};

const statusLabel = {
  matched: 'Matched / Zugeordnet',
  suggested: 'Suggested / Vorgeschlagen',
  recorded: 'Recorded / Verbucht',
  ambiguous: 'Review / Prüfen',
  duplicate: 'Already recorded / Bereits erfasst',
  unmatched: 'Unmatched / Nicht zugeordnet',
  ignored: 'Ignored / Ignoriert',
  outgoing: 'Expense payment / Ausgabe',
};

const loadPlaidLink = () => {
  if (window.Plaid) return Promise.resolve(window.Plaid);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-serviceflow-plaid-link]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.Plaid), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    script.dataset.serviceflowPlaidLink = 'true';
    script.onload = () => resolve(window.Plaid);
    script.onerror = () => reject(new Error('Plaid Link failed to load'));
    document.head.appendChild(script);
  });
};

export default function BankingPage() {
  const { isProPlan, getPlanName } = usePlan();
  const proEnabled = isProPlan();
  const [currency, setCurrency] = useState('CAD');
  const [dateFormat, setDateFormat] = useState('auto');
  const [fileName, setFileName] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState('');
  const [bankingStatus, setBankingStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState('');
  const [updatingAccount, setUpdatingAccount] = useState('');
  const [accountFilter, setAccountFilter] = useState('all');
  const [manualMatchRow, setManualMatchRow] = useState(null);
  const [createExpenseRow, setCreateExpenseRow] = useState(null);

  const loadBankingStatus = useCallback(async () => {
    try {
      const response = await api.get('/banking/status');
      setBankingStatus(response.data);
    } catch (error) {
      setBankingStatus({ provider_connection_available: false, connections: [] });
    }
  }, []);

  useEffect(() => { if (proEnabled) loadBankingStatus(); }, [proEnabled, loadBankingStatus]);

  useEffect(() => {
    const supported = bankingStatus?.supported_currencies || [];
    if (supported.length) setCurrency((current) => supported.includes(current) ? current : supported[0]);
    if (bankingStatus?.region === 'DE_EU') setDateFormat('dd-mm-yyyy');
  }, [bankingStatus]);

  const counts = useMemo(() => rows.reduce((result, row) => {
    result[row.status] = (result[row.status] || 0) + 1;
    return result;
  }, {}), [rows]);

  const readFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = parseBankCsv(await file.text(), { currency, dateFormat });
      setFileName(file.name);
      setTransactions(parsed.transactions);
      setRejected(parsed.rejected);
      setRows([]);
      toast.success(`${parsed.transactions.length} bank transactions loaded`);
    } catch (error) {
      setFileName('');
      setTransactions([]);
      setRows([]);
      toast.error(error.message || 'Could not read this bank CSV');
    } finally {
      event.target.value = '';
    }
  };

  const preview = async (source = transactions) => {
    if (!source.length) return;
    setLoading(true);
    try {
      const response = await api.post('/banking/reconcile-preview', { transactions: source });
      setRows(response.data.rows || []);
      toast.success('Reconciliation preview ready');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not match bank transactions');
    } finally {
      setLoading(false);
    }
  };

  const activeAccounts = useMemo(() => (bankingStatus?.connections || [])
    .filter(connection => connection.status !== 'disconnected')
    .flatMap(connection => connection.bank_accounts || [])
    .filter(account => account.is_active), [bankingStatus]);

  const loadConnectedTransactions = async (filter = accountFilter) => {
    const response = await api.get('/banking/transactions', {
      params: filter === 'all' ? {} : { bank_account_id: filter },
    });
    const imported = response.data || [];
    setFileName('Connected bank accounts');
    setTransactions(imported);
    setRejected([]);
    if (imported.length) await preview(imported);
    else setRows([]);
    return imported.length;
  };

  const changeAccountFilter = async (value) => {
    setAccountFilter(value);
    try {
      await loadConnectedTransactions(value);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not filter bank transactions');
    }
  };

  const toggleBankAccount = async (accountId, isActive) => {
    setUpdatingAccount(accountId);
    try {
      await api.patch(`/banking/accounts/${accountId}`, { is_active: isActive });
      if (!isActive && accountFilter === accountId) setAccountFilter('all');
      await loadBankingStatus();
      await loadConnectedTransactions(!isActive && accountFilter === accountId ? 'all' : accountFilter);
      toast.success(isActive ? 'Account included in matching' : 'Account excluded from matching');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update account selection');
    } finally {
      setUpdatingAccount('');
    }
  };

  const syncConnection = async (connectionId) => {
    setSyncing(connectionId);
    try {
      const response = await api.post(`/banking/connections/${connectionId}/sync`);
      const count = await loadConnectedTransactions();
      await loadBankingStatus();
      toast.success(`${response.data.changed} updates synchronized; ${count} transactions available`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bank synchronization failed');
    } finally {
      setSyncing('');
    }
  };

  const connectBank = async () => {
    setConnecting(true);
    try {
      const Plaid = await loadPlaidLink();
      const response = await api.post('/banking/plaid/link-token');
      const handler = Plaid.create({
        token: response.data.link_token,
        onSuccess: async (publicToken, metadata) => {
          try {
            const linked = await api.post('/banking/plaid/exchange', {
              public_token: publicToken,
              institution_id: metadata.institution?.institution_id || '',
              institution_name: metadata.institution?.name || '',
            });
            toast.success('Bank account connected securely');
            await loadBankingStatus();
            await syncConnection(linked.data.id);
          } catch (error) {
            toast.error(error.response?.data?.detail || 'Could not complete the bank connection');
          } finally {
            setConnecting(false);
          }
        },
        onExit: () => setConnecting(false),
      });
      handler.open();
    } catch (error) {
      setConnecting(false);
      toast.error(error.response?.data?.detail || 'Could not start Plaid Link');
    }
  };

  const disconnectBank = async (connectionId) => {
    if (!window.confirm('Disconnect this bank? Previously imported transactions will remain for audit history.')) return;
    try {
      await api.delete(`/banking/connections/${connectionId}`);
      await loadBankingStatus();
      toast.success('Bank disconnected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not disconnect bank');
    }
  };

  const recordPayment = async (row) => {
    const allocations = row.suggestion?.allocations || [];
    if (!allocations.length || row.status !== 'matched') return;
    setRecording(row.transaction_key);
    try {
      const response = await api.post('/payments', {
        project_id: allocations[0].project_id,
        amount: row.amount,
        payment_method: 'bank_transfer',
        payment_date: `${row.date}T12:00:00`,
        reference_number: (row.reference || row.description || 'Bank deposit').slice(0, 100),
        notes: `Bank statement import: ${row.description}`.slice(0, 500),
        allocations: allocations.map((item) => ({
          invoice_id: item.invoice_id,
          amount: item.amount,
        })),
      });
      if (row.transaction_id) {
        await api.post('/banking/reconcile-existing', {
          transaction_id: row.transaction_id,
          record_type: 'payments',
          record_ids: [response.data.id],
          expected_amount: Math.abs(Number(row.amount)).toFixed(2),
        });
      }
      setRows((current) => current.map((item) => (
        item.transaction_key === row.transaction_key ? { ...item, status: 'recorded' } : item
      )));
      toast.success('Payment recorded and invoice balance updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment could not be recorded');
    } finally {
      setRecording('');
    }
  };

  const recordTransfer = async (row) => {
    const transactionIds = [row.transaction_id, row.suggestion?.paired_transaction_id].filter(Boolean);
    if (!transactionIds.length) return;
    setRecording(row.transaction_key);
    try {
      await api.post('/banking/reconcile-transfer', { transaction_ids: transactionIds });
      const recordedIds = new Set(transactionIds);
      setRows((current) => current.map((item) => (
        recordedIds.has(item.transaction_id) ? { ...item, status: 'recorded' } : item
      )));
      toast.success('Internal transfer reconciled; no income or expense was created');
      window.setTimeout(() => preview(transactions), 0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not reconcile this internal transfer');
    } finally {
      setRecording('');
    }
  };

  const markRowRecorded = (transactionKey) => {
    setRows((current) => current.map((item) => (
      item.transaction_key === transactionKey ? { ...item, status: 'recorded' } : item
    )));
    setManualMatchRow(null);
    setCreateExpenseRow(null);
    if (transactions.some((transaction) => transaction.transaction_id)) {
      window.setTimeout(() => preview(transactions), 0);
    }
  };

  if (!proEnabled) {
    return (
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Banking / Bankabgleich</CardTitle>
          <CardDescription>Automatic payment matching is included in ServiceFlow Pro.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600">Current plan: {getPlanName()}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="banking-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Landmark className="w-8 h-8 text-blue-600" /> Banking / Bankabgleich
        </h1>
        <p className="text-slate-500 mt-1">
          Match Canadian, US, or German/European bank deposits to open invoices before recording payments.
        </p>
        <p className="text-sm text-slate-400">
          Bankeinzahlungen vor der Buchung automatisch offenen Rechnungen zuordnen.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Connected bank accounts / Verbundene Bankkonten</CardTitle>
          <CardDescription>
            Read-only transaction access. ServiceFlow never receives your online-banking password and never moves money.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(bankingStatus?.connections || []).filter(item => item.status !== 'disconnected').map(connection => (
            <div key={connection.id} className="rounded-lg border border-slate-200 p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                <p className="font-medium text-slate-900">{connection.institution_name || 'Connected institution'}</p>
                <p className="text-xs text-slate-400">Status: {connection.status}{connection.last_synced_at ? ` · Last sync: ${new Date(connection.last_synced_at).toLocaleString()}` : ''}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => syncConnection(connection.id)} disabled={syncing === connection.id}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncing === connection.id ? 'animate-spin' : ''}`} />
                    {syncing === connection.id ? 'Syncing...' : 'Sync transactions'}
                  </Button>
                  <Button variant="ghost" onClick={() => disconnectBank(connection.id)}><Unplug className="w-4 h-4 mr-2" />Disconnect</Button>
                </div>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                {(connection.bank_accounts || []).map(account => (
                  <label key={account.id} className={`flex items-center gap-3 rounded-md border px-3 py-2 ${account.is_active ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
                    <input
                      type="checkbox"
                      checked={account.is_active}
                      disabled={updatingAccount === account.id}
                      onChange={(event) => toggleBankAccount(account.id, event.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600"
                    />
                    {account.account_type === 'credit' ? <CreditCard className="h-4 w-4 text-slate-500" /> : <WalletCards className="h-4 w-4 text-slate-500" />}
                    <span className="min-w-0 text-sm">
                      <span className="block truncate font-medium text-slate-800">{account.name}{account.mask ? ` ••••${account.mask}` : ''}</span>
                      <span className="block text-xs capitalize text-slate-500">{account.account_subtype || account.account_type || 'Bank account'} · {account.currency}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={connectBank} disabled={connecting || !bankingStatus?.provider_connection_available}>
              <Link2 className="w-4 h-4 mr-2" />{connecting ? 'Opening Plaid...' : 'Connect bank account'}
            </Button>
            {bankingStatus && !bankingStatus.provider_connection_available && (
              <p className="text-sm text-amber-700">Plaid Sandbox credentials and the encryption key must be configured on SAV-1 first.</p>
            )}
            <span className="text-xs text-slate-400">Environment: {bankingStatus?.provider_environment || 'sandbox'}</span>
            {bankingStatus?.region === 'DE_EU' && bankingStatus?.provider_environment === 'production' && (
              <p className="w-full text-xs text-slate-500">German production banks require European Transactions access on the connected Plaid account.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">1. Import bank statement / Kontoauszug importieren</CardTitle>
          <CardDescription>
            Nothing is stored during preview. Only a payment you explicitly confirm is recorded.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-[160px_200px_1fr] items-end">
            <label className="text-sm font-medium text-slate-700">
              Currency / Währung
              <select className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                <option value="CAD">CAD - Canada</option>
                <option value="USD">USD - United States</option>
                <option value="EUR">EUR - Germany / Europe</option>
              </select>
            </label>
            <label className="text-sm font-medium text-slate-700">
              Date format / Datumsformat
              <select className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3" value={dateFormat} onChange={(e) => setDateFormat(e.target.value)}>
                <option value="auto">Auto (regional)</option>
                <option value="mm-dd-yyyy">MM/DD/YYYY</option>
                <option value="dd-mm-yyyy">DD.MM.YYYY</option>
              </select>
            </label>
            <label className="block">
              <span className="sr-only">Choose bank CSV</span>
              <Input type="file" accept=".csv,text/csv" onChange={readFile} data-testid="bank-csv-input" />
            </label>
          </div>
          {fileName && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
              <div className="text-sm text-blue-900">
                <Upload className="inline w-4 h-4 mr-2" />
                {fileName}: {transactions.length} rows loaded
                {rejected.length > 0 && `, ${rejected.length} rows skipped`}
              </div>
              <Button onClick={() => preview()} disabled={loading || !transactions.length} data-testid="preview-reconciliation">
                <WandSparkles className="w-4 h-4 mr-2" />
                {loading ? 'Matching...' : 'Match payments / Zahlungen zuordnen'}
              </Button>
            </div>
          )}
          {fileName === 'Connected bank accounts' && activeAccounts.length > 0 && (
            <label className="block max-w-sm text-sm font-medium text-slate-700">
              Show transactions from / Transaktionen anzeigen
              <select
                className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3"
                value={accountFilter}
                onChange={(event) => changeAccountFilter(event.target.value)}
              >
                <option value="all">All selected accounts / Alle ausgewählten Konten</option>
                {activeAccounts.map(account => (
                  <option key={account.id} value={account.id}>{account.name}{account.mask ? ` ••••${account.mask}` : ''}</option>
                ))}
              </select>
            </label>
          )}
          {!fileName && (
            <a className="inline-block text-sm text-blue-600 hover:text-blue-700 hover:underline" href="/samples/serviceflow-bank-import-sample.csv" download>
              Download sample CSV / Beispiel-CSV herunterladen
            </a>
          )}
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">2. Review and confirm / Prüfen und bestätigen</CardTitle>
            <CardDescription>
              {counts.matched || 0} matched, {counts.ambiguous || 0} need review, {counts.duplicate || 0} possible duplicates, {counts.unmatched || 0} unmatched.
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date / Datum</TableHead>
                  <TableHead>Description / Beschreibung</TableHead>
                  <TableHead className="text-right">Amount / Betrag</TableHead>
                  <TableHead>Suggested invoice / Vorschlag</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action / Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => {
                  const allocations = row.suggestion?.allocations || [];
                  const expenseSuggestions = row.suggestion?.expenses || [];
                  const paymentSuggestions = row.suggestion?.payments || [];
                  const expenseDraft = row.suggestion?.expense_draft;
                  const isTransfer = row.suggestion?.record_type === 'transfer';
                  const isIncoming = Number(row.amount) > 0;
                  const displayedStatus = expenseDraft && row.status === 'matched'
                    ? 'suggested'
                    : (!isIncoming && row.status === 'ignored' ? 'outgoing' : row.status);
                  return (
                    <TableRow key={`${row.transaction_key}-${index}`}>
                      <TableCell className="whitespace-nowrap">{row.date}</TableCell>
                      <TableCell className="min-w-[220px]">
                        <div className="font-medium text-slate-800">{row.description || '-'}</div>
                        {row.reference && <div className="text-xs text-slate-500">Ref: {row.reference}</div>}
                      </TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap">{money(row.amount, row.currency)}</TableCell>
                      <TableCell className="min-w-[260px]">
                        {isTransfer ? (
                          <div className="text-sm">
                            <span className="font-medium">Internal transfer / Credit-card payment</span>
                            <span className="text-slate-500"> · {row.suggestion?.paired_account_name}</span>
                          </div>
                        ) : allocations.length > 0 ? allocations.map((item) => (
                          <div key={item.invoice_id} className="text-sm">
                            <span className="font-medium">{item.invoice_number}</span>
                            <span className="text-slate-500"> · {item.client_name} · {money(item.amount, row.currency)}</span>
                          </div>
                        )) : paymentSuggestions.length > 0 ? paymentSuggestions.map((item) => (
                          <div key={item.payment_id} className="text-sm">
                            <span className="font-medium">Existing payment {item.payment_date}</span>
                            <span className="text-slate-500"> · {money(item.amount, row.currency)}</span>
                          </div>
                        )) : expenseSuggestions.length > 0 ? expenseSuggestions.map((item) => (
                          <div key={item.expense_id} className="text-sm">
                            <span className="font-medium">{item.description}</span>
                            <span className="text-slate-500"> · {item.vendor || 'Vendor'} · {money(item.amount, row.currency)}</span>
                          </div>
                        )) : expenseDraft ? (
                          <div className="text-sm">
                            <span className="font-medium">Category: {expenseDraft.category}</span>
                            <span className="text-slate-500"> · Vendor: {expenseDraft.vendor}</span>
                          </div>
                        ) : <span className="text-slate-400">No suggestion / Kein Vorschlag</span>}
                        {row.status === 'duplicate' && row.duplicate_payment && (
                          <div className="text-xs text-slate-500">Existing payment: {row.duplicate_payment.payment_date}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusStyle[displayedStatus] || statusStyle.unmatched}`}>
                          {statusLabel[displayedStatus] || displayedStatus}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {isTransfer && row.status === 'matched' && (
                            <Button size="sm" onClick={() => recordTransfer(row)} disabled={recording === row.transaction_key}>
                              {recording === row.transaction_key ? 'Recording...' : 'Confirm transfer'}
                            </Button>
                          )}
                          {!isTransfer && isIncoming && row.status === 'matched' && allocations.length > 0 && (
                            <Button size="sm" onClick={() => recordPayment(row)} disabled={recording === row.transaction_key}>
                              {recording === row.transaction_key ? 'Recording...' : 'Confirm / Buchen'}
                            </Button>
                          )}
                          {!isTransfer && isIncoming && !['duplicate', 'recorded'].includes(row.status) && (
                            <Button size="sm" variant="outline" onClick={() => setManualMatchRow(row)}>
                              Allocate invoices
                            </Button>
                          )}
                          {!isTransfer && !isIncoming && !['duplicate', 'recorded'].includes(row.status) && (
                            <Button size="sm" variant={expenseSuggestions.length ? 'default' : 'outline'} onClick={() => setManualMatchRow(row)}>
                              Match existing expense
                            </Button>
                          )}
                          {!isTransfer && !isIncoming && !['duplicate', 'recorded'].includes(row.status) && (
                            <Button size="sm" variant={expenseDraft && !expenseSuggestions.length ? 'default' : 'outline'} onClick={() => setCreateExpenseRow(row)}>
                              Categorize expense
                            </Button>
                          )}
                        </div>
                        {row.status === 'recorded' && <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" />}
                        {row.status === 'ambiguous' && <AlertCircle className="w-5 h-5 text-amber-600 ml-auto" />}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-slate-400">
        CSV previews remain in this browser session. Connected-bank provider tokens are encrypted at rest; online-banking passwords are never handled by ServiceFlow.
      </p>

      <BankAllocationDialog
        key={manualMatchRow?.transaction_key || 'closed'}
        row={manualMatchRow}
        open={!!manualMatchRow}
        onClose={() => setManualMatchRow(null)}
        onRecorded={markRowRecorded}
        onCreateExpense={() => {
          const selectedRow = manualMatchRow;
          setManualMatchRow(null);
          setCreateExpenseRow(selectedRow);
        }}
      />
      <BankExpenseDialog
        key={createExpenseRow?.transaction_key || 'closed-expense'}
        row={createExpenseRow}
        open={!!createExpenseRow}
        onClose={() => setCreateExpenseRow(null)}
        onRecorded={markRowRecorded}
      />
    </div>
  );
}

function BankAllocationDialog({ row, open, onClose, onRecorded, onCreateExpense }) {
  const [candidates, setCandidates] = useState([]);
  const [clients, setClients] = useState({});
  const [projects, setProjects] = useState({});
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingMode, setExistingMode] = useState(Boolean(row?.suggestion?.existing));
  const [expenseView, setExpenseView] = useState('all');
  const [expenseSearch, setExpenseSearch] = useState('');

  const incoming = Number(row?.amount || 0) > 0;
  const target = Math.abs(Number(row?.amount || 0));
  const selectedCandidates = candidates.filter((item) => selected[item.id] !== undefined);
  const selectedGroup = selectedCandidates.length
    ? (incoming ? selectedCandidates[0].client_id : (selectedCandidates[0].vendor || '').trim().toLowerCase())
    : '';
  const selectedTotal = selectedCandidates.reduce(
    (sum, item) => sum + Number(incoming && !existingMode ? selected[item.id] : item.amount || 0),
    0,
  );
  const balances = Math.abs(selectedTotal - target) < 0.005;
  const filteredCandidates = incoming ? candidates : candidates.filter((item) => {
    const query = expenseSearch.trim().toLowerCase();
    if (!query) return true;
    return [item.description, item.vendor, item.expense_date, item.amount, item.category]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query));
  });

  useEffect(() => {
    if (!open || !row) return;
    setLoading(true);
    setSelected({});
    setExpenseSearch('');
    const request = incoming
      ? (existingMode
        ? Promise.all([api.get('/payments'), api.get('/invoices'), api.get('/clients'), api.get('/projects')])
        : Promise.all([api.get('/invoices'), api.get('/clients'), api.get('/projects')]))
      : Promise.all([api.get('/expenses', { params: { paid_status: expenseView } })]);
    request.then((responses) => {
      if (incoming) {
        if (existingMode) {
          const invoiceById = Object.fromEntries((responses[1].data || []).map((invoice) => [invoice.id, invoice]));
          const existingPayments = (responses[0].data || []).map((payment) => {
            const invoiceIds = (payment.allocations || []).map((allocation) => allocation.invoice_id);
            if (!invoiceIds.length && payment.invoice_id) invoiceIds.push(payment.invoice_id);
            const paymentInvoices = invoiceIds.map((id) => invoiceById[id]).filter(Boolean);
            const clientIds = [...new Set(paymentInvoices.map((invoice) => invoice.client_id))];
            const invoiceNumbers = (payment.allocations || []).map((allocation) => allocation.invoice_number).filter(Boolean);
            return {
              ...payment,
              client_id: clientIds.length === 1 ? clientIds[0] : `payment-${payment.id}`,
              display_name: invoiceNumbers.length ? invoiceNumbers.join(', ') : 'Recorded payment',
            };
          });
          setCandidates(existingPayments);
          setClients(Object.fromEntries((responses[2].data || []).map((client) => [client.id, client.name])));
          setProjects(Object.fromEntries((responses[3].data || []).map((project) => [project.id, project.name])));
        } else {
          const openInvoices = (responses[0].data || []).filter((invoice) => (
            !['paid', 'void'].includes(invoice.status)
            && Number(invoice.balance_due || 0) > 0
            && (!invoice.currency || invoice.currency === row.currency)
          ));
          setCandidates(openInvoices);
          setClients(Object.fromEntries((responses[1].data || []).map((client) => [client.id, client.name])));
          setProjects(Object.fromEntries((responses[2].data || []).map((project) => [project.id, project.name])));
        }
      } else {
        setCandidates(responses[0].data || []);
      }
      const suggestionMatchesMode = incoming
        ? Boolean(row.suggestion?.existing) === existingMode
        : expenseView === 'all' || Boolean(row.suggestion?.existing) === (expenseView === 'paid');
      if (suggestionMatchesMode) {
        if (incoming && !existingMode && row.suggestion?.allocations?.length) {
          setSelected(Object.fromEntries(row.suggestion.allocations.map((item) => [item.invoice_id, item.amount])));
        } else if (row.suggestion?.record_ids?.length) {
          setSelected(Object.fromEntries(row.suggestion.record_ids.map((id) => [id, true])));
        }
      }
    }).catch((error) => {
      toast.error(error.response?.data?.detail || 'Could not load matching records');
      setCandidates([]);
    }).finally(() => setLoading(false));
  }, [open, row, incoming, existingMode, expenseView]);

  const toggleCandidate = (item, checked) => {
    setSelected((current) => {
      const next = { ...current };
      if (checked) next[item.id] = incoming && !existingMode ? Number(item.balance_due || 0).toFixed(2) : true;
      else delete next[item.id];
      return next;
    });
  };

  const candidateGroup = (item) => incoming
    ? item.client_id
    : `${item.paid ? 'paid' : 'unpaid'}:${(item.vendor || '').trim().toLowerCase()}`;

  const save = async () => {
    if (!balances || !selectedCandidates.length) return;
    setSaving(true);
    const reference = (row.reference || row.transaction_key || row.description || 'Bank transaction').slice(0, 100);
    try {
      const selectedPaidExpenses = !incoming && selectedCandidates.every((item) => item.paid);
      if ((incoming && existingMode) || selectedPaidExpenses) {
        await api.post('/banking/reconcile-existing', {
          transaction_id: row.transaction_id || row.reference,
          record_type: incoming ? 'payments' : 'expenses',
          record_ids: selectedCandidates.map((item) => item.id),
          expected_amount: target.toFixed(2),
        });
        toast.success('Bank transaction linked to records already marked paid');
      } else if (incoming) {
        const response = await api.post('/payments', {
          project_id: selectedCandidates[0].project_id,
          amount: target.toFixed(2),
          payment_method: 'bank_transfer',
          payment_date: `${row.date}T12:00:00`,
          reference_number: reference,
          notes: `Bank reconciliation: ${row.description}`.slice(0, 500),
          allocations: selectedCandidates.map((invoice) => ({
            invoice_id: invoice.id,
            amount: Number(selected[invoice.id]).toFixed(2),
          })),
        });
        if (row.transaction_id) {
          await api.post('/banking/reconcile-existing', {
            transaction_id: row.transaction_id,
            record_type: 'payments',
            record_ids: [response.data.id],
            expected_amount: target.toFixed(2),
          });
        }
        toast.success('Bank deposit allocated to selected invoices');
      } else {
        await api.post('/expenses/mark-paid-batch', {
          expense_ids: selectedCandidates.map((expense) => expense.id),
          expected_amount: target.toFixed(2),
          paid_at: `${row.date}T12:00:00`,
          paid_method: 'bank_transfer',
          paid_reference: reference,
        });
        if (row.transaction_id) {
          await api.post('/banking/reconcile-existing', {
            transaction_id: row.transaction_id,
            record_type: 'expenses',
            record_ids: selectedCandidates.map((expense) => expense.id),
            expected_amount: target.toFixed(2),
          });
        }
        toast.success('Bank payment matched to selected contractor expenses');
      }
      onRecorded(row.transaction_key);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record this allocation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {incoming ? 'Allocate customer payment to invoices' : 'Match bank transaction to an existing expense'}
          </DialogTitle>
          <DialogDescription>
            {row?.date} · {row?.description} · {money(target, row?.currency)}
          </DialogDescription>
        </DialogHeader>

        {incoming ? (
          <div className="flex gap-2">
            <Button type="button" size="sm" variant={existingMode ? 'outline' : 'default'} onClick={() => setExistingMode(false)}>
              Open invoices
            </Button>
            <Button type="button" size="sm" variant={existingMode ? 'default' : 'outline'} onClick={() => setExistingMode(true)}>
              Already marked paid
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {[
                ['all', 'All expenses'],
                ['unpaid', 'Unpaid expenses'],
                ['paid', 'Already marked paid'],
              ].map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={expenseView === value ? 'default' : 'outline'}
                  onClick={() => { setExpenseView(value); setSelected({}); }}
                >
                  {label}
                </Button>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={onCreateExpense}>
                Categorize as a new expense
              </Button>
            </div>
            <Input
              value={expenseSearch}
              onChange={(event) => setExpenseSearch(event.target.value)}
              placeholder="Search vendor, invoice/description, date, amount or category"
            />
          </div>
        )}

        <div className="flex items-center justify-between rounded-md border bg-slate-50 px-4 py-3 text-sm">
          <span>Selected: <strong>{money(selectedTotal, row?.currency)}</strong></span>
          <span>Bank transaction: <strong>{money(target, row?.currency)}</strong></span>
          <span className={balances ? 'text-emerald-700' : 'text-amber-700'}>
            {balances ? 'Balanced' : `Difference: ${money(target - selectedTotal, row?.currency)}`}
          </span>
        </div>

        <div className="max-h-[430px] overflow-y-auto rounded-md border">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : filteredCandidates.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {incoming
                ? (existingMode ? 'No recorded payments available' : 'No open invoices available')
                : (expenseSearch ? 'No expenses match this search' : 'No expenses available')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>{incoming ? (existingMode ? 'Payment / Client' : 'Invoice / Client') : 'Expense / Vendor'}</TableHead>
                  <TableHead>{incoming && !existingMode ? 'Project' : 'Date'}</TableHead>
                  <TableHead className="text-right">{incoming && !existingMode ? 'Balance' : 'Amount'}</TableHead>
                  {incoming && !existingMode && <TableHead className="text-right w-36">Allocate</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.map((item) => {
                  const checked = selected[item.id] !== undefined;
                  const disabled = !!selectedGroup && candidateGroup(item) !== selectedGroup && !checked;
                  return (
                    <TableRow key={item.id} className={disabled ? 'opacity-45' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          disabled={disabled}
                          onCheckedChange={(value) => toggleCandidate(item, value === true)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{incoming ? (existingMode ? item.display_name : item.invoice_number) : item.description}</div>
                        <div className="text-xs text-slate-500">
                          {incoming ? (clients[item.client_id] || 'Client') : (item.vendor || 'No vendor')}
                          {!incoming && <span> · {item.paid ? 'Paid' : 'Unpaid'}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {incoming && !existingMode
                          ? (projects[item.project_id] || 'Project')
                          : String(incoming ? item.payment_date : item.expense_date || '').slice(0, 10)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {money(incoming && !existingMode ? item.balance_due : item.amount, row?.currency)}
                      </TableCell>
                      {incoming && !existingMode && (
                        <TableCell>
                          <Label className="sr-only">Allocation for {item.invoice_number}</Label>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            max={item.balance_due}
                            disabled={!checked}
                            value={checked ? selected[item.id] : ''}
                            onChange={(event) => setSelected((current) => ({ ...current, [item.id]: event.target.value }))}
                            className="text-right"
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>

        <p className="text-xs text-slate-500">
          {incoming
            ? (existingMode
              ? 'This links the bank deposit to existing payment records without changing invoice balances.'
              : 'A single customer payment may cover multiple invoices for the same client.')
            : 'All existing expenses are available. Select unpaid expenses to mark them paid, or paid expenses to link them without changing their accounting. Paid and unpaid records cannot be mixed in one allocation.'}
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!balances || !selectedCandidates.length || saving}>
            {saving ? 'Recording...' : 'Confirm allocation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BankExpenseDialog({ row, open, onClose, onRecorded }) {
  const draft = row?.suggestion?.expense_draft || {};
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vendor: draft.vendor || '',
    description: draft.description || row?.description || '',
    category: draft.category || 'office',
    project_id: '',
    tax_amount: '0.00',
  });

  useEffect(() => {
    if (!open) return;
    api.get('/projects').then((response) => setProjects(response.data || [])).catch(() => setProjects([]));
  }, [open]);

  const save = async () => {
    if (!form.description.trim()) return;
    setSaving(true);
    try {
      await api.post('/banking/create-expense', {
        transaction_id: row.transaction_id || row.reference,
        description: form.description.trim(),
        vendor: form.vendor.trim(),
        category: form.category,
        project_id: form.project_id || null,
        tax_amount: Number(form.tax_amount || 0).toFixed(2),
      });
      toast.success('Business expense created and bank transaction reconciled');
      onRecorded(row.transaction_key);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create this expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Categorize bank transaction as an expense</DialogTitle>
          <DialogDescription>{row?.date} · {money(Math.abs(Number(row?.amount || 0)), row?.currency)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Vendor</Label>
            <Input value={form.vendor} onChange={(event) => setForm({ ...form, vendor: event.target.value })} placeholder="OpenAI, Adobe, Microsoft..." />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Expense category</Label>
              <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>
                <option value="office">Office / Software</option>
                <option value="travel">Travel</option>
                <option value="equipment">Equipment</option>
                <option value="materials">Materials</option>
                <option value="labor">Labor</option>
                <option value="subcontractor">Subcontractor</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Tax included in amount</Label>
              <Input type="number" min="0" step="0.01" value={form.tax_amount} onChange={(event) => setForm({ ...form, tax_amount: event.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Project (optional)</Label>
            <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={form.project_id} onChange={(event) => setForm({ ...form, project_id: event.target.value })}>
              <option value="">General business expense</option>
              {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
            </select>
          </div>
          <p className="text-xs text-slate-500">The amount and date come from the bank and cannot be changed here. Confirm what the purchase was for; ServiceFlow will remember this vendor and category for future transactions even when the amount changes.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.description.trim()}>{saving ? 'Creating...' : 'Create expense and reconcile'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
